import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map, filter, tap, mergeMap } from 'rxjs/operators';
import { of as ObservableOf } from 'rxjs/observable/of';
import {
  filter as _filter,
  isNil as _isNil,
  map as _map,
  isEmpty as _isEmpty,
  get as _get,
  first as _first,
} from 'lodash';
import * as moment from 'moment';

import { DateTimeUtils } from '@blcloud/bl-common';
import {
  ICustomerInfo,
  ICustomerVehicleSearch,
  IItemServiceSetting,
  IVehicleInfo,
  IWebReserve,
  IWebReserveLinkReq,
  IWebReserveLinkCandidate,
  ISnsPotentialCustomer,
  IOperationHistoryLog,
  ISnsShopAccountSetting,
} from '@blcloud/bl-datamodel';
import {
  CustomerInfoResource,
  ItemServiceSettingResource,
  VehicleInfoResource,
  WebReserveResource,
  WebReserveLinkReqResource,
  WebReserveLinkCandidateResource,
  SnsPotentialCustomerResource,
  OperationHistoryLogResource,
  SnsShopAccountSettingResource
} from '@blcloud/bl-ng-resource';
import {
  BlApiOptions,
  BlConditionInfo,
  BlConditionType,
  BlEntityPathOptions,
} from '@blcloud/bl-ng-common';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { DealsConst } from '@blcloud/bl-datamodel/const/deals';
import { ProductCode } from '@blcloud/bl-datamodel/enum/common/product-code';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { OpeHistoryKindDiv } from '@blcloud/bl-datamodel/enum/bizcmn/ope-history-kind-div';
import { OperationContentDiv } from '@blcloud/bl-datamodel/enum/bizcmn/operation-content-div';
import { BlDialogService } from '@blcloud/bl-ng-ui-component';
import { CustomerVehicleSearchResourceService, LoginResourceService } from '@blcloud/bl-ng-share-module';

import { SnsDefinitionMap, SnsFollowerCalledDefault } from '../../core/const/sns-define';
import { SnsPotentialCustomerLinkActionType, WebReserveCustomerVehicleLinkFunctionName } from './web-reserve-customer-vehicle-link.define';


/**
 * Web予約顧客車両紐付サービス
 */
@Injectable()
export class WebReserveCustomerVehicleLinkService {

  /** 一覧取得件数 */
  private totalCount = 0;

  /** 契約中のプロダクトコードリスト */
  productCodeList: string[] = [];

  constructor(
    private customerInfoResource: CustomerInfoResource,
    private customerVehicleSearchResourceService: CustomerVehicleSearchResourceService,
    private dialogService: BlDialogService,
    private itemServicesettingResource: ItemServiceSettingResource,
    private vehicleInfoResource: VehicleInfoResource,
    private webReserveResource: WebReserveResource,
    private webReserveLinkReqResource: WebReserveLinkReqResource,
    private webReserveLinkCandidateResource: WebReserveLinkCandidateResource,
    private loginResource: LoginResourceService,
    private snsShopAccountResource: SnsShopAccountSettingResource,
    private snsPotentialCustomerResource: SnsPotentialCustomerResource,
    private operationHistoryLogResource: OperationHistoryLogResource,
  ) { }

  /**
   * 商品サービス設定を検索する
   * @param blTenantId BLテナントID
   * @param itemServiceNum 商品サービス番号
   * @return 商品サービス設定
   */
  getItemServiceSetting(blTenantId: string, itemServiceNum: number): Observable<IItemServiceSetting> {
    // 検索条件の初期化
    const itemServiceSetting: IItemServiceSetting = {
      blTenantId: blTenantId,
      itemServiceNum: itemServiceNum
    };

    return this.itemServicesettingResource.getById(
      this.itemServicesettingResource.getIdString(itemServiceSetting),
      { subject: 'common'});
  }

  /**
   * Web予約情報を検索する
   * @param blTenantId BLテナントID
   * @param webReserveNum Web予約番号
   * @return Web予約情報
   */
  getWebReserve(blTenantId: string, webReserveNum: string): Observable<IWebReserve> {
    // 検索条件の初期化
    const webReserve: IWebReserve = {
      webReserveBlTenantId: blTenantId,
      webReserveNum: webReserveNum
    };

    // Web予約情報の検索を実行
    return this.webReserveResource.getById(this.webReserveResource.getIdString(webReserve), { subject: 'common'});
  }

  /**
   * 顧客情報を検索する
   * @param blTenantId BLテナントID
   * @param customerCode 顧客コード
   * @return 顧客情報
   */
  getCustomerInfo(blTenantId: string, customerCode: number): Observable<ICustomerInfo> {
    // 顧客情報
    const customerInfo: ICustomerInfo = {
      blTenantId,
      customerCode
    };
    // 顧客情報の検索を実行
    return this.customerInfoResource.getById(this.customerInfoResource.getIdString(customerInfo), { subject: 'common'});
  }

  /**
   * 車両情報を検索する
   * @param blTenantId BLテナントID
   * @param vehicleMgtNumber 車両管理番号
   * @return 顧客情報
   */
  getVehicleInfo(blTenantId: string, vehicleMgtNumber: number): Observable<IVehicleInfo> {
    // 車両情報
    const vehicleInfo: IVehicleInfo = {
      blTenantId,
      vehicleMgtNumber
    };
    // 車両情報の検索を実行
    return this.vehicleInfoResource.getById(this.vehicleInfoResource.getIdString(vehicleInfo), { subject: 'common'});
  }

  /**
   * 検索数を指定して紐付ける顧客・車両情報を検索する
   * @param webReserveNum Web予約番号
   * @param size 検索数
   */
  getCustomerVehicle(webReserveNum: string, size: number): Observable<IWebReserveLinkCandidate[]> {
    const conditions: BlConditionInfo[] = [
      { key: 'webReserveNum', value: webReserveNum, type: BlConditionType.Equal },
    ];
    const sortFields = [{ field: 'webReserveLinkScore', desc: true }];

    return this.webReserveLinkCandidateResource.search<IWebReserveLinkCandidate>(
      { conditions: conditions, sortFields: sortFields, size: size },
      { entityOptions: { exPath: 'q/bywebreserve' } }
    ).pipe(
      map(response => {
        this.totalCount = response ? response.totalCount : 0;
        return response ? response.searchResultList : [];
      })
    );
  }

  /**
   * 一覧データ件数を取得します。
   * @returns 一覧データ件数
   */
  getTotalCount(): number {
    return this.totalCount;
  }

  /**
   * SNS店舗アカウント設定を取得します。
   * パラメータが不正な場合、nullが出力されるオブザーバブルを返します。
   *
   * @param snsBlTenantId テナントID
   * @param snsDiv SNS区分
   * @param snsLinkageId SNS連携ID
   * @returns SNS店舗アカウント設定オブザーバブル
   */
   getSnsShopAccountSetting$(snsBlTenantId: string, snsDiv: string, snsLinkageId: string): Observable<ISnsShopAccountSetting> {
    // 必要条件が欠落しているなら検索しない
    if (_isEmpty(snsBlTenantId) || _isEmpty(snsDiv) || _isEmpty(snsLinkageId)) {
      return ObservableOf<ISnsPotentialCustomer>(null);
    }

    const id = this.snsShopAccountResource.getIdString({
      snsBlTenantId,
      snsDiv,
      snsLinkageId,
    });
    const apiOptions: BlApiOptions = {
      anonymous: true
    };

    return this.snsShopAccountResource.getById<ISnsShopAccountSetting>(id, { apiOptions });
  }

  /**
   * SNS見込客情報を取得します。
   * パラメータが不正な場合、nullが出力されるオブザーバブルを返します。
   *
   * @param snsBlTenantId テナントID
   * @param snsLinkageId SNS連携ID
   * @param snsId SNSID
   * @returns SNS見込客情報オブザーバブル
   */
  getSnsPotentialCustomer$(snsBlTenantId: string, snsLinkageId: string, snsId: string): Observable<ISnsPotentialCustomer> {
    // 必要条件が欠落しているなら検索しない
    if (_isEmpty(snsBlTenantId) || _isEmpty(snsLinkageId) || _isEmpty(snsId)) {
      return ObservableOf<ISnsPotentialCustomer>(null);
    }

    const id = this.snsPotentialCustomerResource.getIdString({
      snsBlTenantId,
      snsLinkageId,
      snsId
    });
    const apiOptions: BlApiOptions = {
      anonymous: true
    };

    return this.snsPotentialCustomerResource.getById<ISnsPotentialCustomer>(id, { apiOptions });
  }

  /**
   * web予約情報からSNS見込客情報を取得します。
   * SNSからの予約でない場合やサービス利用不可の場合、nullが出力されるオブザーバブルが返ります。
   *
   * @param webReserve Web予約情報
   * @returns SNS見込客情報オブザーバブル
   */
  getSnsPotentialCustomerFromWebReserve$(webReserve: IWebReserve): Observable<ISnsPotentialCustomer> {
    // 必要条件が欠落しているなら検索しない
    if (_isNil(webReserve)) {
      return ObservableOf<ISnsPotentialCustomer>(null);
    }

    const snsDefinitions = _get(SnsDefinitionMap, webReserve.snsDiv);
    if (_isNil(snsDefinitions) || _isEmpty(snsDefinitions.serviceId)) {
      // SNS区分がない場合も空を返す
      return ObservableOf<ISnsPotentialCustomer>(null);
    }

    return this.loginResource.isAvailableService(snsDefinitions.serviceId).mergeMap(
      isAvailable => {
        if (isAvailable) {
          return this.getSnsPotentialCustomer$(webReserve.webReserveBlTenantId, webReserve.snsLinkageId, webReserve.snsId);
        } else {
          // サービス利用不可の場合も空を返す
          return ObservableOf<ISnsPotentialCustomer>(null);
        }
      }
    );
  }

  /**
   * 顧客の下書き情報を取得します。
   *
   * 顧客コードが0または空の場合はストリームはnull
   *
   * @param customer 見込客情報
   * @returns 顧客情報オブザーバブル
   */
  getCustomerInfoDraft$(customerCode: number): Observable<ICustomerInfo> {
    if (customerCode === 0) {
      return ObservableOf(null);
    }
    const conditions: any[][] = [
      [
        { key: 'customerCode', value: String(customerCode), type: BlConditionType.Equal }
      ]
    ];

    const entityOptions: BlEntityPathOptions = {
      operation: 'draft'
    };
    const options = {
      consistent: true,
      subject: 'all',
      entityOptions
    };
    return this.customerInfoResource.search<ICustomerInfo>({ conditions }, options).pipe(
      map(
        response => {
          if (!_isNil(response)) {
            // ヒットした場合は先頭の要素
            return _first(response.searchResultList);
          } else {
            return null;
          }
        }
      )
    );
  }

  /**
   * 顧客情報が編集中かどうかをチェックします。
   *
   * @param customer 顧客情報
   * @returns true: 編集中, false: 編集中でない(変更可能である)
   */
  customerIsEditing$(customerCode: number): Observable<boolean> {
    return this.getCustomerInfoDraft$(customerCode).pipe(
      map(
        response => !_isNil(response)
      )
    );
  }

  /**
   * SNS見込客と顧客の紐付けます。
   *
   * @param entity SNS見込客
   * @param customer 顧客
   * @param shopAccount 店舗アカウント
   * @param pageId 画面ID
   * @returns 紐付け済SNS見込客オブザーバブル
   */
  linkSnsPotentialCustomer$(entity: ISnsPotentialCustomer, customer: ICustomerInfo,
                            shopAccount: ISnsShopAccountSetting,
                            pageId: string): Observable<ISnsPotentialCustomer> {
    const id = this.snsPotentialCustomerResource.getIdString(entity);
    return this.snsPotentialCustomerResource.getById<ISnsPotentialCustomer>(id).pipe(
      mergeMap(
        latestPotentialCustomer => {
          latestPotentialCustomer.customerCode = customer.customerCode;
          return this.putSnsPotentialCustomer$(latestPotentialCustomer).pipe(
            tap(
              updatedPotentialCustomer => {
                this.postOperationHistoryLog(updatedPotentialCustomer,
                                             customer,
                                             shopAccount.snsShopAccountName,
                                             SnsPotentialCustomerLinkActionType.Link, pageId,
                                             WebReserveCustomerVehicleLinkFunctionName);
              }
            )
          );
        }
      )
    );
  }

  /**
   * SNS見込客と顧客の紐付けを解除します。
   *
   * @param entity SNS見込客
   * @param customer 顧客
   * @param shopAccount 店舗アカウント
   * @param pageId 画面ID
   * @returns 紐付け済SNS見込客オブザーバブル
   */
  unlinkSnsPotentialCustomer$(entity: ISnsPotentialCustomer, customer: ICustomerInfo,
                              shopAccount: ISnsShopAccountSetting,
                              pageId: string): Observable<ISnsPotentialCustomer> {
    const id = this.snsPotentialCustomerResource.getIdString(entity);
    return this.snsPotentialCustomerResource.getById<ISnsPotentialCustomer>(id).pipe(
      mergeMap(
        latestPotentialCustomer => {
          latestPotentialCustomer.customerCode = 0;
          return this.putSnsPotentialCustomer$(latestPotentialCustomer).pipe(
            tap(
              updatedPotentialCustomer => {
                this.postOperationHistoryLog(updatedPotentialCustomer,
                                             customer,
                                             shopAccount.snsShopAccountName,
                                             SnsPotentialCustomerLinkActionType.Unlink, pageId,
                                             WebReserveCustomerVehicleLinkFunctionName);
              }
            )
          );
        }
      )
    );
  }

  /**
   * SNS見込客情報を更新します。
   *
   * @param entity エンティティ
   *
   * @returns 更新結果オブザーバブル
   */
  putSnsPotentialCustomer$(entity: ISnsPotentialCustomer): Observable<ISnsPotentialCustomer> {
    return this.snsPotentialCustomerResource.putByEntity<ISnsPotentialCustomer>(entity);
  }

  /**
   * Web予約情報と顧客・車両情報の候補を紐付ける
   *
   * @param webReserveLinkReq Web予約情報紐付要求
   */
  linkWebReserve(webReserveLinkReq: IWebReserveLinkReq): Observable<IWebReserveLinkReq> {
    return this.webReserveLinkReqResource.post<IWebReserveLinkReq>(
      webReserveLinkReq,
      {
        subject: 'common',
      },
      ).pipe(
        map(
          response => {
            return response;
          }
        ),
        catchError(err => {
          this.dialogService.info(err.messages[0])
            .subscribe(blModalRef => {
              blModalRef.hide();
            });
          return ObservableOf(undefined);
        })
      );
  }

  /**
   * Web予約情報と顧客・車両情報の候補を紐付ける（DM用）
   *
   * @param webReserveLinkReqs Web予約情報紐付要求配列
   */
  linkWebReserveByDm(webReserveLinkReq: IWebReserveLinkReq): Observable<IWebReserveLinkReq> {
    return this.webReserveLinkReqResource.post<IWebReserveLinkReq>(
      webReserveLinkReq,
      {
        subject: 'common',
        entityOptions: {
          exPath: 'linkDmReserve'
        },
      },
      ).pipe(
        map(
          response => {
            return response;
          }
        ),
        catchError(err => {
          this.dialogService.info(err.messages[0])
            .subscribe(blModalRef => {
              blModalRef.hide();
            });
          return ObservableOf(undefined);
        })
      );
  }

  /**
   * 顧客・車両情報を復活
   *
   * @param customerCode 顧客コード
   */
  restoration(customerCode: number): Observable<ICustomerVehicleSearch> {
    return this.customerVehicleSearchResourceService.restoration(customerCode);
  }

  /**
   * 顧客・車両情報の車両側を復活
   *
   * @param customerCode 顧客コード
   * @param vehicleMgtNumber 車両管理番号
   */
  vehicleRestoration(customerCode: number, vehicleMgtNumber: number): Observable<ICustomerVehicleSearch> {
    return this.customerVehicleSearchResourceService.vehicleRestoration(customerCode, vehicleMgtNumber);
  }

  /**
   * 対象のプロダクトコード取得
   */
  productCodeList$(): Observable<any> {
    return this.loginResource.getAvailableMainServiceIds()
      .pipe(
        map(res => _map(res, id => this.serviceIdToProductCode(id))),
        filter(productCode => !_isNil(productCode)),
        tap(productCodeList => {
          // 契約中かつ対象のプロダクトコード
          this.productCodeList = _filter(productCodeList, code => {
            return code === ProductCode.SuperFrontman || code === ProductCode.Repair || code === ProductCode.CarSales;
          });
        })
      );
  }

  /**
   * 操作ログを書き込みます。
   *
   * @param potentialCustomer SNS見込客情報
   * @param customerInfo 顧客情報
   * @param pageId 画面ID
   * @param functionName 機能名
   */
  postOperationHistoryLog(potentialCustomer: ISnsPotentialCustomer, customerInfo: ICustomerInfo,
                          shopAccountName: string,
                          linkActionType: SnsPotentialCustomerLinkActionType,
                          pageId: string, functionName: string) {
    const snsDefinitions = _get(SnsDefinitionMap, potentialCustomer.snsDiv);
    const followerCalled = snsDefinitions ? snsDefinitions.followerCalled : SnsFollowerCalledDefault;
    const customerName = customerInfo.customerName1 || '' + customerInfo.customerName2;

    let message = '';
    switch (linkActionType) {
      case SnsPotentialCustomerLinkActionType.Link:
        // tslint:disable-next-line: max-line-length
        message = `「${shopAccountName}」の${followerCalled}「${potentialCustomer.snsDisplayName}」を顧客「${customerInfo.customerCode}:${customerName}」に紐付けました。`;
        break;
      case SnsPotentialCustomerLinkActionType.Unlink:
        // tslint:disable-next-line: max-line-length
        message = `「${shopAccountName}」の${followerCalled}「${potentialCustomer.snsDisplayName}」と顧客「${customerInfo.customerCode}:${customerName}」の紐付けを解除しました。`;
        break;
    }
    if (_isEmpty(message)) {
      // メッセージが空になる場合は何もしない
      return;
    }
    // 操作ログAPIをコール
    const body: IOperationHistoryLog = {
      productCode: ProductCode.Common,
      pageId,
      opeHistoryKindDiv: OpeHistoryKindDiv.Record,
      opeHistoryFunctionName: functionName,
      operationContentDiv: OperationContentDiv.DataUpdate,
      opeHistoryMessageList: [message],
      subjects: {
        sf: {
          customerCode: customerInfo.customerCode
        }
      }
    };
    this.operationHistoryLogResource.post(body, {}).subscribe(
      () => {},
      () => {}
    );
  }

  /**
   * サービスIDをプロダクトコードに変換します。
   * @param serviceId サービスID
   * @returns プロダクトコード
   */
  private serviceIdToProductCode(serviceId: string): string {
    switch (serviceId) {
      case ContractConst.SERVICEID_PRODUCT_MAINTENANCE:
        return ProductCode.SuperFrontman;
      case ContractConst.SERVICEID_PRODUCT_REPAIR:
        return ProductCode.Repair;
    }

    return null;
  }

  get inProgressReceptionConditions(): BlConditionInfo[] {
    // 契約中のプロダクトコードを設定
    const conditions: BlConditionInfo[] = [
      {
        key: 'updateDateTime',
        value: moment('0', 'H').add(-3, 'years').format(DateTimeUtils.formatString.datetime),
        type: BlConditionType.GreaterThan
      },
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
      { key: 'sysInternalLabelList', value: [DealsConst.SS_PROPOSAL_UNSETTLED], type: BlConditionType.NotEqual },
      { key: 'estimatesSlipList', value: null, type: BlConditionType.NotEqual },
      { key: 'salesOrderSlip', value: null, type: BlConditionType.NotEqual },
      { key: 'salesSlip', value: null, type: BlConditionType.NotEqual },
    ];
    if (this.productCodeList.length) {
      conditions.push({ key: 'productCode', value: this.productCodeList, type: BlConditionType.In });
    }
    return conditions;
  }

}



// WEBPACK FOOTER //
// ./src/app/feature/web-reserve-customer-vehicle-link/web-reserve-customer-vehicle-link.service.ts